import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { hasEmailSignatureContent, sanitizeEmailSignatureHtml } from './email-signature-html';

type SignatureFormatCommand = 'bold' | 'italic' | 'justifyCenter' | 'justifyLeft' | 'justifyRight' | 'underline';

interface SignatureClipboardItem {
    readonly types: ReadonlyArray<string>;
    getType(type: string): Promise<Blob>;
}

interface SignatureClipboard {
    read?: () => Promise<ReadonlyArray<SignatureClipboardItem>>;
    readText?: () => Promise<string>;
}

@Component({
    selector: 'vf-email-signature-editor',
    templateUrl: './email-signature-editor.component.html',
    styleUrls: ['./email-signature-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailSignatureEditorComponent implements AfterViewInit, OnChanges {
    private static readonly AllowedImageTypes: ReadonlySet<string> = new Set([
        'image/gif', 'image/jpeg', 'image/png', 'image/webp'
    ]);
    private static readonly ClipboardBlockedMessage =
        'Your browser blocked clipboard access — copy your signature again and retry.';
    private static readonly MaxImageBytes = 1024 * 1024;

    @Input() value = '';
    @Input() ariaLabel = 'Email signature';
    @Input() pasteOnly = false;
    @Output() valueChange = new EventEmitter<string>();

    @ViewChild('editor') private editor: ElementRef<HTMLDivElement> | undefined;
    @ViewChild('imageInput') private imageInput: ElementRef<HTMLInputElement> | undefined;
    @ViewChild('pasteTarget') private pasteTarget: ElementRef<HTMLDivElement> | undefined;

    errorMessage = '';
    pasteOnlyHasContent = false;
    pasteOnlyEmpty = true;
    pasteOnlyBusy = false;
    pasteOnlyIdle = true;
    pasteOnlyHtml = '';

    constructor(
        private renderer: Renderer2,
        private changeDetector: ChangeDetectorRef
    ) {}

    ngAfterViewInit(): void {
        this.syncValueToEditor();
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.syncValueToEditor();
    }

    chooseImage(): void {
        if (this.imageInput) {
            this.imageInput.nativeElement.click();
        }
    }

    onFormatMouseDown(event: MouseEvent, command: SignatureFormatCommand): void {
        event.preventDefault();
        this.executeCommand(command);
    }

    onLinkMouseDown(event: MouseEvent): void {
        event.preventDefault();
        if (!this.editor) {
            return;
        }

        const view = this.editor.nativeElement.ownerDocument.defaultView;
        const address = view ? view.prompt('Link address') : null;
        if (!address) {
            return;
        }

        this.executeCommand('createLink', address.trim());
    }

    onImageMouseDown(event: MouseEvent): void {
        event.preventDefault();
        this.chooseImage();
    }

    onImageSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length ? input.files[0] : null;
        if (file) {
            this.addImageFile(file);
        }
        input.value = '';
    }

    onInput(): void {
        this.emitCurrentValue();
    }

    onPaste(event: ClipboardEvent): void {
        const clipboard = event.clipboardData;
        if (this.pasteOnly) {
            event.preventDefault();
            this.importClipboardData(clipboard);
            return;
        }

        if (!clipboard) {
            return;
        }

        const html = clipboard.getData('text/html');
        if (html) {
            event.preventDefault();
            const cleanHtml = sanitizeEmailSignatureHtml(html);
            this.insertHtmlAtCaret(cleanHtml);
            this.emitCurrentValue();
            return;
        }

        const imageFile = Array.from(clipboard.files).find((file: File) => file.type.indexOf('image/') === 0);
        if (imageFile) {
            event.preventDefault();
            this.addImageFile(imageFile);
        }
    }

    clearPastedSignature(): void {
        this.errorMessage = '';
        this.updatePasteOnlyState('');
        this.valueChange.emit('');
    }

    async pasteFromClipboard(): Promise<void> {
        if (this.pasteOnlyBusy) {
            return;
        }

        const view = this.pasteTarget && this.pasteTarget.nativeElement.ownerDocument.defaultView;
        const clipboard = view
            ? view.navigator.clipboard as unknown as SignatureClipboard
            : undefined;
        if (!clipboard) {
            this.showError(EmailSignatureEditorComponent.ClipboardBlockedMessage);
            return;
        }

        this.errorMessage = '';
        this.pasteOnlyBusy = true;
        this.pasteOnlyIdle = false;
        this.changeDetector.markForCheck();
        try {
            if (clipboard.read) {
                const items = await clipboard.read();
                let html = '';
                let text = '';
                let image: Blob | undefined;
                for (const item of items) {
                    if (!html && item.types.indexOf('text/html') >= 0) {
                        html = await (await item.getType('text/html')).text();
                    }
                    if (!text && item.types.indexOf('text/plain') >= 0) {
                        text = await (await item.getType('text/plain')).text();
                    }
                    const imageType = item.types.find((type: string) =>
                        EmailSignatureEditorComponent.AllowedImageTypes.has(type));
                    if (!image && imageType) {
                        image = await item.getType(imageType);
                    }
                }

                if (this.tryImportHtmlOrText(html, text)) {
                    return;
                }
                if (image) {
                    await this.replaceWithImageBlob(image);
                    return;
                }
            } else if (clipboard.readText) {
                const text = await clipboard.readText();
                if (this.tryImportHtmlOrText('', text)) {
                    return;
                }
            }

            this.showError('Nothing to paste yet — copy your signature from Outlook first.');
        } catch (_error) {
            this.showError(EmailSignatureEditorComponent.ClipboardBlockedMessage);
        } finally {
            this.pasteOnlyBusy = false;
            this.pasteOnlyIdle = true;
            this.changeDetector.markForCheck();
        }
    }

    private importClipboardData(clipboard: DataTransfer | null): void {
        if (!clipboard) {
            this.showError('Use Ctrl+V to paste the signature you copied from Outlook.');
            return;
        }

        const html = clipboard.getData('text/html');
        const text = clipboard.getData('text/plain');
        if (this.tryImportHtmlOrText(html, text)) {
            return;
        }

        const image = Array.from(clipboard.files).find((file: File) =>
            EmailSignatureEditorComponent.AllowedImageTypes.has(file.type));
        if (image) {
            this.replaceWithImageBlob(image).catch(() => this.showError("This image couldn't be read."));
            return;
        }

        this.showError('Nothing to paste yet — copy your signature from Outlook first.');
    }

    private tryImportHtmlOrText(html: string, text: string): boolean {
        const cleanHtml = sanitizeEmailSignatureHtml(html || '');
        if (hasEmailSignatureContent(cleanHtml)) {
            this.setPastedSignature(cleanHtml);
            return true;
        }

        const textHtml = this.plainTextToHtml(text || '');
        if (hasEmailSignatureContent(textHtml)) {
            this.setPastedSignature(textHtml);
            return true;
        }

        return false;
    }

    private setPastedSignature(html: string): void {
        const cleanHtml = sanitizeEmailSignatureHtml(html || '');
        if (!hasEmailSignatureContent(cleanHtml)) {
            this.showError('Nothing to paste yet — copy your signature from Outlook first.');
            return;
        }

        this.errorMessage = '';
        this.updatePasteOnlyState(cleanHtml);
        this.valueChange.emit(cleanHtml);
    }

    private async replaceWithImageBlob(image: Blob): Promise<void> {
        if (!EmailSignatureEditorComponent.AllowedImageTypes.has(image.type)) {
            this.showError('Paste a PNG, JPG, GIF, or WebP image.');
            return;
        }
        if (image.size > EmailSignatureEditorComponent.MaxImageBytes) {
            this.showError('Signature images must be 1 MB or smaller.');
            return;
        }

        const source = await this.readBlobAsDataUrl(image);
        this.setPastedSignature(
            '<img src="' + source + '" alt="Pasted signature image" style="max-width:100%;height:auto">'
        );
    }

    private readBlobAsDataUrl(blob: Blob): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => typeof reader.result === 'string'
                ? resolve(reader.result)
                : reject(new Error('Image data was unavailable.'));
            reader.onerror = () => reject(reader.error || new Error('Image data could not be read.'));
            reader.readAsDataURL(blob);
        });
    }

    private plainTextToHtml(text: string): string {
        const escaped = (text || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/\r\n|\r|\n/g, '<br>');
        return escaped ? '<div>' + escaped + '</div>' : '';
    }

    private syncValueToEditor(): void {
        const cleanValue = sanitizeEmailSignatureHtml(this.value || '');
        this.updatePasteOnlyState(cleanValue);
        if (!this.editor) {
            return;
        }

        if (this.editor.nativeElement.innerHTML !== cleanValue) {
            this.renderer.setProperty(this.editor.nativeElement, 'innerHTML', cleanValue);
        }
    }

    private updatePasteOnlyState(cleanValue: string): void {
        this.pasteOnlyHasContent = hasEmailSignatureContent(cleanValue);
        this.pasteOnlyEmpty = !this.pasteOnlyHasContent;
        this.pasteOnlyHtml = this.pasteOnlyHasContent ? cleanValue : '';
        this.changeDetector.markForCheck();
    }

    private addImageFile(file: File): void {
        this.errorMessage = '';
        if (!EmailSignatureEditorComponent.AllowedImageTypes.has(file.type)) {
            this.showError('Choose a PNG, JPG, GIF, or WebP image.');
            return;
        }
        if (file.size > EmailSignatureEditorComponent.MaxImageBytes) {
            this.showError('Signature images must be 1 MB or smaller.');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            if (!this.editor || typeof reader.result !== 'string') {
                return;
            }

            const image = this.renderer.createElement('img') as HTMLImageElement;
            this.renderer.setAttribute(image, 'src', reader.result);
            this.renderer.setAttribute(image, 'alt', file.name || 'Signature image');
            this.renderer.setStyle(image, 'max-width', '100%');
            this.renderer.setStyle(image, 'height', 'auto');
            this.renderer.appendChild(this.editor.nativeElement, image);
            this.emitCurrentValue();
        };
        reader.onerror = () => this.showError("This image couldn't be read.");
        reader.readAsDataURL(file);
    }

    private insertHtmlAtCaret(html: string): void {
        if (!html || !this.editor) {
            return;
        }

        const host = this.editor.nativeElement;
        const documentNode = host.ownerDocument;
        const selection = documentNode.defaultView ? documentNode.defaultView.getSelection() : null;
        if (!selection || selection.rangeCount === 0 || !host.contains(selection.getRangeAt(0).commonAncestorContainer)) {
            const holder = this.renderer.createElement('div') as HTMLDivElement;
            this.renderer.setProperty(holder, 'innerHTML', html);
            while (holder.firstChild) {
                this.renderer.appendChild(host, holder.firstChild);
            }
            return;
        }

        const range = selection.getRangeAt(0);
        range.deleteContents();
        const fragment = range.createContextualFragment(html);
        const lastNode = fragment.lastChild;
        range.insertNode(fragment);
        if (lastNode) {
            range.setStartAfter(lastNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }

    private emitCurrentValue(): void {
        if (!this.editor) {
            return;
        }
        const cleanValue = sanitizeEmailSignatureHtml(this.editor.nativeElement.innerHTML);
        this.valueChange.emit(cleanValue);
    }

    private executeCommand(command: string, value?: string): void {
        if (!this.editor) {
            return;
        }

        const host = this.editor.nativeElement;
        host.focus();
        host.ownerDocument.execCommand(command, false, value || null);
        this.emitCurrentValue();
    }

    private showError(message: string): void {
        this.errorMessage = message;
        this.changeDetector.markForCheck();
    }
}
